// Google Identity Services + Sign in with Apple JS helpers.
// Both return an OpenID Connect ID token that the API verifies.

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
export const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';
// Must exactly match a Return URL registered on the Apple Services ID.
export const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI
    || (typeof window !== 'undefined' ? `${window.location.origin}/login` : '');

export const ssoProviders = () => [
    GOOGLE_CLIENT_ID && 'google',
    APPLE_CLIENT_ID && 'apple',
].filter(Boolean);

export const PROVIDER_LABELS = { google: 'Google', apple: 'Apple' };

const GOOGLE_SCRIPT = 'https://accounts.google.com/gsi/client';
const APPLE_SCRIPT = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const scriptPromises = {};
function loadScript(src) {
    if (!scriptPromises[src]) {
        scriptPromises[src] = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.defer = true;
            script.onload = resolve;
            script.onerror = () => {
                delete scriptPromises[src];
                reject(new Error(`Failed to load ${src}`));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromises[src];
}

/** Renders Google's official button into `element`; calls onToken(idToken). */
export async function renderGoogleButton(element, onToken, { text = 'signin_with', width } = {}) {
    await loadScript(GOOGLE_SCRIPT);
    const google = window.google;
    google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => response?.credential && onToken(response.credential),
        ux_mode: 'popup',
        auto_select: false,
        cancel_on_tap_outside: true,
    });
    element.innerHTML = '';
    google.accounts.id.renderButton(element, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text,
        logo_alignment: 'center',
        ...(width ? { width } : {}),
    });
}

/** Opens the Apple sign-in popup; resolves with the ID token, or null if cancelled. */
export async function signInWithApple() {
    await loadScript(APPLE_SCRIPT);
    window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'email',
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
    });
    try {
        const response = await window.AppleID.auth.signIn();
        return response?.authorization?.id_token || null;
    } catch (err) {
        if (err?.error === 'popup_closed_by_user' || err?.error === 'user_cancelled_authorize') {
            return null;
        }
        throw err;
    }
}
